import { QueryFunction, QueryKey, useQuery, useQueryClient } from '@tanstack/vue-query'
import { AccessorKeyColumnDef, VisibilityState } from '@tanstack/vue-table'
import { computed, onMounted, ref, watch } from 'vue'
import { usePejerBase } from './stores/pejerData'
import { PejerTableNames, PejerTablesList } from './types/base'
import type { BaseQueryArgs, ErrorObject, Order, PaginationArgs, SortingArgs } from './types/types'
import usePagination from './usePagination'
import useQueryParams from './useQueryParams'
import { getSavedDataTableVisibleColumns, saveVisibleDataTableColumns } from './utils'

export type TableFeatureEnabled = 'custom' | boolean

export type PejerTableListOptions<T extends PejerTableNames> = {
  default_query?: Partial<BaseQueryArgs<PejerTablesList<T>>>
  pageSize?: number
  columns?: AccessorKeyColumnDef<PejerTablesList<T>, unknown>[]
  pagesLoadedAtATime?: number
  url?: string
  urlKey?: string
  // Hidden columns key
  colVisibilityOptions?: {
    key?: string
    defaultColumns?: string[]
    dontSave?: boolean
  }
  resetPagination?: (newSize: number) => void
  showAll?: boolean
}

const DEFAULT_PAGE_SIZE = 10
const QUERY_STALE_TIME = 1000 * 60 * 5 // 5 minutes

export default function usePejerTableList<T extends PejerTableNames>(
  pejerTableName: T,
  options?: PejerTableListOptions<T>
) {
  type LIST = PejerTablesList<T>
  const store = usePejerBase()

  const dataQuerySize =
    (options?.pageSize ?? DEFAULT_PAGE_SIZE) * (options?.pagesLoadedAtATime || 3)

  const { state, updateParams } = useQueryParams<SortingArgs<LIST>>(
    {
      sorter: options?.default_query?.sorter ?? 'Descending',
      sortBy:
        options?.default_query?.sortBy ??
        (store.getDefaultColumns(pejerTableName).sortingColumn as keyof LIST),
    },
    {
      sorter: 'sort',
      sortBy: 'by',
    },
    {
      key: options?.urlKey,
    }
  )

  const {
    initialPage,
    initialPageSize,
    isCurrentPageLoaded,
    totalSize,
    reset,
    setItemsLoaded,
    updateIfCloseToEdge,
    ...paginationProps
  } = usePagination(1, options?.pageSize ?? DEFAULT_PAGE_SIZE, updateQuery, {
    key: options?.urlKey,
    prefetchQuery: prefetchQuery,
  })

  const currentQuery = ref<BaseQueryArgs<LIST>>({
    filters: {
      staticFilters: options.default_query.filters?.staticFilters || {},
      userFilters: options.default_query.filters?.staticFilters || {},
    },
    page: Math.floor(((initialPage - 1) * initialPageSize) / dataQuerySize) + 1,
    size: dataQuerySize,
    ...options?.default_query,
    ...state.value,
  } as BaseQueryArgs<LIST>)

  const queryKeyBase = [pejerTableName, 'list'] as QueryKey

  const queryClient = useQueryClient()
  const queryKeyPaginationOmitted = computed(() => {
    const { page, ...rest } = currentQuery.value
    return [...queryKeyBase, rest] as QueryKey
  })

  const queryKeyPageIndex = queryKeyPaginationOmitted.value.length
  const getQueryKey = (page: number) => {
    return [...queryKeyPaginationOmitted.value, page] as QueryKey
  }

  const isQueryEnabled = ref(false)

  const queryFn: QueryFunction<LIST[], readonly {}[], number> = async (a) => {
    const currentParams = {
      ...currentQuery.value,
      page: a.queryKey[queryKeyPageIndex] as number,
    } as BaseQueryArgs<LIST>

    const res = await store.getMany<T, LIST>(pejerTableName, currentParams, {
      url: options?.url,
      abortSignal: a.signal,
    })

    if ('error' in res) throw res
    setItemsLoaded(res.data.length, currentParams)
    return res.data
  }

  const currentQueryKey = computed(() => {
    return getQueryKey(currentQuery.value.page)
  })

  const { data, refetch, promise, ...queryProps } = useQuery<LIST[], ErrorObject>({
    queryKey: currentQueryKey,
    queryFn,
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: isQueryEnabled,
    staleTime: QUERY_STALE_TIME,
    experimental_prefetchInRender: true,
  })

  watch(
    () => paginationProps.currentPage.value.page,
    (val) => {
      updateQuery({
        ...currentQuery.value,
        page: paginationProps.translateFromLocalPageToQueryPage(val),
      } as BaseQueryArgs<LIST>)
    }
  )

  const initialColumnVisibilityState = ref(getInitialColumnVisibilityState())
  const defaultVisibleColumns = ref(getDefaultVisibleColumns())
  function getDefaultVisibleColumns() {
    const columns =
      options?.columns || (store.columns[pejerTableName].list as AccessorKeyColumnDef<LIST>[])

    let cols = {} as VisibilityState

    const newMap = {} as VisibilityState
    if (!options?.colVisibilityOptions.defaultColumns)
      columns.forEach((col) => {
        const isMetaVisible = !col.meta?.hide_default
        if (isMetaVisible) {
          cols[(col.id || (col.accessorKey as string)).replace(/\./g, '_')] = true
        }
      })

    cols = { ...newMap, ...cols }

    for (const col of options?.colVisibilityOptions?.defaultColumns || []) {
      const formattedCol = col.replace(/\./g, '_')
      if (formattedCol in cols) continue
      cols[formattedCol] = true
    }

    return cols
  }

  function getInitialColumnVisibilityState() {
    // Gets the currently saved columns
    let cols = options?.colVisibilityOptions?.dontSave
      ? {}
      : getSavedDataTableVisibleColumns(getHiddenColumnsKey())

    const defaultCols = getDefaultVisibleColumns()
    cols = { ...defaultCols, ...cols }
    return cols
  }

  function updateColumnVisibilityState(columns: VisibilityState, forceUpdate = false) {
    if (forceUpdate) defaultVisibleColumns.value = columns

    // Remove everything that is the same as the default columns
    const updatedCols: VisibilityState = {}
    const defaultCols = defaultVisibleColumns.value
    for (const key in columns) {
      if (key in columns === false || !columns[key] !== !defaultCols[key])
        updatedCols[key] = columns[key]
    }
    saveVisibleDataTableColumns(getHiddenColumnsKey(), updatedCols)
  }

  function getHiddenColumnsKey() {
    return (
      pejerTableName +
      (options?.colVisibilityOptions?.key ? '-' + options.colVisibilityOptions?.key : '')
    )
  }

  const prefetchQueryQueue = ref<Promise<any>[]>([])
  async function prefetchQuery(pagination: PaginationArgs) {
    await promise.value
    const res = queryClient.fetchQuery({
      queryKey: getQueryKey(pagination.page),
      queryFn,
      staleTime: QUERY_STALE_TIME,
      retry: 1,
    })

    prefetchQueryQueue.value.push(res)
    await res
    // Find and remove my promise from the queue
    prefetchQueryQueue.value = prefetchQueryQueue.value.filter((p) => p !== res)
  }

  async function refresh() {
    // We need to invalidate the whole table
    await queryClient.invalidateQueries({
      queryKey: queryKeyBase,
    })
  }

  function triggerSort(column: keyof LIST | null, direction: Order = 'Descending') {
    const newQuery: Partial<BaseQueryArgs<LIST>> = {
      sortBy: column ?? store.getDefaultColumns(pejerTableName).sortingColumn,
      sorter: direction,
    }
    updateParams({ sortBy: column, sorter: direction })
    resetQuery(newQuery)
  }

  function search(query: Partial<BaseQueryArgs<LIST>>, shouldResetPagination = true) {
    const newQuery: BaseQueryArgs<LIST> = {
      ...(currentQuery.value as BaseQueryArgs<LIST>),
      filters: {},
      ...query,
    }
    if (shouldResetPagination) resetQuery(newQuery)
    else currentQuery.value = newQuery
    if (!isQueryEnabled.value) isQueryEnabled.value = true
  }

  async function updateQuery(query?: Partial<BaseQueryArgs<LIST>>) {
    await promise.value
    const cachedData = getCachedData()

    // There should always be data
    if (!cachedData) return

    currentQuery.value = {
      ...currentQuery.value,
      ...query,
    } as BaseQueryArgs<LIST>

    if (!isQueryEnabled.value) isQueryEnabled.value = true
  }

  function getCachedData() {
    const cachedData = queryClient.getQueriesData<LIST[]>({
      queryKey: queryKeyPaginationOmitted.value,
      exact: false,
      stale: false,
      // predicate: (query) => {
      //   console.log('getCachedData predicate', {
      //     query,
      //     queryKeyPaginationOmitted: queryKeyPaginationOmitted.value,
      //   })
      //   return query.queryKey.every((key, i) => {
      //     if (i === queryKeyPageIndex) return true
      //     return deepCompare(key, queryKeyPaginationOmitted.value[i])
      //   })
      // },
    })

    if (!cachedData || cachedData.length === 0) return

    const res: {
      [key: number]: LIST[]
    } = {}

    for (let i = 0; i < cachedData.length; i++) {
      const [queryKey, data] = cachedData[i]
      const pageNumber = queryKey[queryKeyPageIndex] as number
      if (!data) continue
      res[pageNumber] = data
    }

    return res
  }

  function setItemsLoadedBasedOnCachedData() {
    const data = getCachedData()
    if (!data) return

    Object.entries(data).forEach(([key, value]) => {
      setItemsLoaded(value.length, {
        page: Number(key),
        size: currentQuery.value.size,
      })
    })
  }

  onMounted(() => {
    setItemsLoadedBasedOnCachedData()
    updateIfCloseToEdge(paginationProps.currentPage.value.page)
  })

  function resetQuery(query?: Partial<BaseQueryArgs<LIST>>) {
    currentQuery.value = {
      ...currentQuery.value,
      size: options?.pageSize || dataQuerySize,
      ...(query || {}),
      page: 1,
    } as BaseQueryArgs<LIST>
    reset()
    setItemsLoadedBasedOnCachedData()
  }

  const visibleData = computed(() => {
    if (!data.value) return []

    const localPage =
      (paginationProps.currentPage.value.page - 1) % paginationProps.pagesLoadedAtATime

    const visibleItems = data.value.slice(
      localPage * paginationProps.currentPage.value.size,
      (localPage + 1) * paginationProps.currentPage.value.size
    )

    // console.log('visibleData computed', visibleItems)

    // We need to get the correct subset of the data based on the current local page and the query page
    return visibleItems
  })

  // Loading state
  const isLoadingCurrentPage = computed(() => {
    return !isCurrentPageLoaded.value && queryProps.isLoading.value
  })

  const isFetching = computed(
    () => prefetchQueryQueue.value.length > 0 || queryProps.isFetching.value
  )

  return {
    ...queryProps,
    ...paginationProps,
    isLoading: isLoadingCurrentPage,
    isFetching,
    data: visibleData,
    query: currentQuery,
    totalSize,
    defaultColumnVisibilityState: defaultVisibleColumns,
    initialColumnVisibilityState,
    search,
    reloadPages: refresh,
    reload: refresh,
    refresh,
    triggerSort,
    updateColumnVisibilityState,
    resetData: resetQuery,
  }
}
